/**
 * SiddhiPage — the ceremonial culmination of a book's journey.
 *
 * Renders the identity statement + 4-stanza WOOP retrospective distilled
 * from the user's lived CraftPrint reflections. Backed by three
 * Siddhi-A endpoints:
 *   • GET  /siddhi/{install_id}                  (read persisted)
 *   • POST /siddhi/generate                      (produce fresh)
 *   • PATCH /siddhi/{install_id}/identity        (refine in user's voice)
 *
 * States rendered:
 *   (a) no book param                → EmptyState (StageEmptyStateFlank)
 *   (b) 428 gate — no CraftPrint OR reflections unedited
 *                                    → NeedsSadhana warm nudge
 *   (c) loading                      → LoadingBanner
 *   (d) LLM/backend error            → ErrorState with retry
 *   (e) persisted Siddhi found       → ceremonial layout
 *                                       identity statement (editable inline)
 *                                       four WOOP stanzas (read-only cards)
 *                                       optional narrative journal + regenerate
 *                                       "Begin another journey" → /library
 *
 * Tone: temple, not gym. No metrics. No scoring. No celebration. The
 * user does not "earn" a Siddhi, they receive one.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAutosave } from "../lib/useAutosave";
import { RealSiddhiRetro, EarlierVersions, SealAsChapter } from "../components/mycraftz/RealSiddhiRetro";
import { siddhiText, LANG_LABEL } from "../lib/siddhiText";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, RefreshCw, BookOpen } from "lucide-react";

import Navigation from "../components/homev3/Navigation";
import Footer from "../components/homev3/Footer";
import GoldRule from "../components/homev3/GoldRule";
import ParchmentBackground from "../components/ParchmentBackground";
import { useLanguage } from "../lib/useLanguage";
import { useAuth } from "../context/AuthContext";
import { readAnonCompletions, SADHANA_TARGET_DAYS } from "../lib/sadhanaCompletion";
import { T, R } from "../components/homev3/tokens";
import {
  CARD_BG,
  CARD_BORDER,
  CARD_RADIUS,
  CARD_SHADOW,
  StageKicker,
  StageBottomBanner,
  StageEmptyStateFlank,
} from "../components/homev3/parchmentCard";
import { resolveBook } from "../lib/bookLookup";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install`;
const AXIOS_TIMEOUT_MS = 60000;
const GENERATE_TIMEOUT_MS = 150000;

// ─────────────────────────────────────────────────────────────────────
//  Small shared bits — mirror SadhanaPage tone
// ─────────────────────────────────────────────────────────────────────

function Lotus({ size = 42 }) {
  // Simple ceremonial ornament — a gold lotus glyph rendered as an inline
  // SVG so it scales cleanly and needs no asset fetch. Sits above the
  // identity statement to signal arrival.
  return (
    <svg
      width={size}
      height={size * 0.6}
      viewBox="0 0 60 36"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <g fill="none" stroke={T.GOLD} strokeWidth="1.2" strokeLinecap="round">
        <path d="M30 34 C 18 30, 8 22, 6 12 C 14 14, 22 20, 30 30 Z" fill="rgba(201,168,76,0.14)" />
        <path d="M30 34 C 42 30, 52 22, 54 12 C 46 14, 38 20, 30 30 Z" fill="rgba(201,168,76,0.14)" />
        <path d="M30 34 C 24 26, 22 16, 24 4 C 28 10, 30 20, 30 30 Z" fill="rgba(201,168,76,0.22)" />
        <path d="M30 34 C 36 26, 38 16, 36 4 C 32 10, 30 20, 30 30 Z" fill="rgba(201,168,76,0.22)" />
      </g>
    </svg>
  );
}

function LoadingBanner({ bookTitle }) {
  return (
    <div
      data-testid="siddhi-loading"
      style={{
        margin: "24px auto",
        padding: "18px 24px",
        maxWidth: 620,
        textAlign: "center",
        background: "rgba(201,168,76,0.08)",
        border: "1px dashed rgba(201,168,76,0.55)",
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "center",
      }}
    >
      <Lotus size={36} />
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 14,
          color: T.DARK_BROWN,
        }}
      >
        Receiving your Siddhi from <strong>{bookTitle}</strong>…
      </p>
      <p className="m-0" data-testid="siddhi-loading-wait" style={{ fontFamily: T.F_BODY, fontSize: 13, color: T.DARK_BROWN, opacity: 0.75 }}>
        Distilling your Siddhi… this can take up to a minute.
      </p>
    </div>
  );
}

function ErrorState({ bookTitle, onRetry }) {
  return (
    <div
      data-testid="siddhi-error"
      style={{
        margin: "24px auto",
        padding: "22px 28px",
        maxWidth: 620,
        textAlign: "center",
        background: "rgba(139,58,18,0.06)",
        border: `1px solid rgba(139,58,18,0.3)`,
        borderRadius: 8,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontSize: 14,
          color: T.DARK_BROWN,
          lineHeight: 1.55,
          marginBottom: 12,
        }}
      >
        A quiet interruption while distilling your Siddhi from{" "}
        <strong>{bookTitle}</strong>. Please try again in a moment.
      </p>
      <button
        type="button"
        data-testid="siddhi-retry"
        onClick={onRetry}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 18px",
          fontFamily: T.F_HEADING,
          fontSize: 12,
          letterSpacing: "0.08em",
          color: T.DARK_BROWN,
          background: "rgba(240,230,204,0.7)",
          border: `1px solid ${T.GOLD}`,
          borderRadius: 999,
          cursor: "pointer",
        }}
      >
        <RefreshCw size={13} strokeWidth={1.75} /> Try again
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Gate view — when the backend returns 428 (no CraftPrint or reflections
//  not yet user-edited). Non-punitive; sends the reader back to Sadhana.
// ─────────────────────────────────────────────────────────────────────

function NeedsSadhana({ book, gate }) {
  const reason = gate?.reason;
  const missing = Array.isArray(gate?.missing) ? gate.missing : [];
  return (
    <div
      data-testid="siddhi-needs-sadhana"
      style={{
        maxWidth: 640,
        margin: "0 auto",
        padding: "40px 24px",
        textAlign: "center",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
        <Lotus size={48} />
      </div>
      <StageKicker stage="siddhi" text="SIDDHI · सिद्धि" testId="siddhi-header-kicker" />
      <h1
        className="m-0"
        style={{
          fontFamily: T.F_DISPLAY,
          fontSize: 34,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.15,
          marginBottom: 14,
        }}
      >
        Siddhi awaits your reflection.
      </h1>
      <p
        style={{
          fontFamily: T.F_BODY,
          fontSize: 16,
          fontStyle: "italic",
          color: T.MUTED_BROWN,
          lineHeight: 1.6,
          maxWidth: 520,
          margin: "0 auto 22px",
        }}
      >
        {reason === "no_craftprint" ? (
          <>
            No practice yet from <strong>{book.title}</strong>. Begin with{" "}
            <Link to={`/sankalpa?book=${book.id}`} style={{ color: T.DARK_BROWN, textDecorationColor: T.GOLD }}>
              Sankalpa
            </Link>{" "}
            to commit your three, then return here after Sadhana.
          </>
        ) : (
          <>
            Siddhi is distilled from lived practice, not initial guesses.
            Name your Reward (<em style={{ color: T.GOLD }}>Phala</em>) and
            Obstacle (<em style={{ color: T.GOLD }}>Vighna</em>) for:{" "}
            <strong data-testid="siddhi-gate-titles">
              {missing.map((m) => m.title).filter(Boolean).join(", ") || "each practice"}
            </strong>
            .
          </>
        )}
      </p>
      {reason === "reflections_incomplete" && missing.length > 0 && (
        <div
          data-testid="siddhi-gate-missing"
          style={{
            display: "inline-block",
            padding: "14px 20px",
            background: "rgba(255,248,224,0.7)",
            border: `1px dashed ${T.GOLD}`,
            borderRadius: 8,
            fontFamily: T.F_BODY,
            fontSize: 13,
            color: T.DARK_BROWN,
            textAlign: "left",
            marginBottom: 28,
          }}
        >
          <p className="m-0" style={{ fontStyle: "italic", marginBottom: 8, color: T.MUTED_BROWN }}>
            Still to reflect on:
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {missing.map((m, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                <strong>{m.title || `Practice ${i + 1}`}</strong> —{" "}
                {m.missing_fields.map((f, j) => (
                  <React.Fragment key={f}>
                    {j > 0 && " · "}
                    <Link
                      to={`/sadhana?book=${book.id}&focus=${f}-${m.idea_id}`}
                      data-testid={`siddhi-goto-${f}-${m.idea_id}`}
                      style={{ color: T.DARK_BROWN, textDecorationColor: T.GOLD }}
                    >
                      Name your {f === "phala" ? "Reward (Phala)" : "Obstacle (Vighna)"}
                    </Link>
                  </React.Fragment>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div style={{ marginTop: 18 }}>
        <Link
          to={missing[0]
            ? `/sadhana?book=${book.id}&focus=${missing[0].missing_fields[0]}-${missing[0].idea_id}`
            : `/sadhana?book=${book.id}`}
          data-testid="siddhi-goto-sadhana"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 26px",
            fontFamily: T.F_HEADING,
            fontSize: 13,
            letterSpacing: "0.10em",
            color: T.DARK_BROWN,
            background: "rgba(240,230,204,0.8)",
            border: `1px solid ${T.GOLD}`,
            borderRadius: 999,
            textDecoration: "none",
          }}
        >
          {missing.length ? "Name your Reward & Obstacle" : "Return to Sadhana"} <ArrowRight size={14} strokeWidth={1.75} />
        </Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Empty state — no book param at all
// ─────────────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      data-testid="siddhi-empty"
      style={{
        maxWidth: 620,
        margin: "0 auto",
        padding: "80px 24px",
        textAlign: "center",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
        <Lotus size={48} />
      </div>
      <StageKicker stage="siddhi" text="SIDDHI · सिद्धि" size="large" />
      <h1
        className="m-0 siddhi-empty-h1"
        style={{
          fontFamily: T.F_DISPLAY,
          fontSize: 38,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.15,
          marginBottom: 14,
        }}
      >
        A Siddhi arrives book by book.
      </h1>
      <p
        style={{
          fontFamily: T.F_BODY,
          fontSize: 16,
          fontStyle: "italic",
          color: T.MUTED_BROWN,
          lineHeight: 1.6,
          maxWidth: 460,
          margin: "0 auto 24px",
        }}
      >
        Choose a book from your library. Complete its Bodha, Sankalpa,
        and Sadhana. The Siddhi will follow, quietly.
      </p>
      <Link
        to="/library"
        data-testid="siddhi-goto-library"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 26px",
          fontFamily: T.F_HEADING,
          fontSize: 13,
          letterSpacing: "0.10em",
          color: T.DARK_BROWN,
          background: "rgba(240,230,204,0.8)",
          border: `1px solid ${T.GOLD}`,
          borderRadius: 999,
          textDecoration: "none",
        }}
      >
        <BookOpen size={14} strokeWidth={1.75} /> Open the Library
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Current-book panel — mirrors Sadhana's left column
// ─────────────────────────────────────────────────────────────────────

function CurrentBookPanel({ book, generatedAt }) {
  const generatedLabel = useMemo(() => {
    if (!generatedAt) return null;
    try {
      const d = new Date(generatedAt);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return null; }
  }, [generatedAt]);

  return (
    <aside
      data-testid="siddhi-current-book"
      className="siddhi-current-book"
      style={{
        background: CARD_BG,
        border: CARD_BORDER,
        borderRadius: CARD_RADIUS,
        padding: 20,
        boxShadow: CARD_SHADOW,
        position: "sticky",
        top: 84,
        maxHeight: "calc(100vh - 108px)",
        minWidth: 0,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 11,
          letterSpacing: "0.16em",
          color: T.GOLD,
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        CURRENT BOOK
      </p>
      <div
        style={{
          aspectRatio: "3/4",
          background: "#EADFC4",
          overflow: "hidden",
          borderRadius: 6,
          border: `1px solid ${T.GOLD}`,
          marginBottom: 14,
        }}
      >
        <img
          src={book.coverUrl || book.image}
          alt={book.title}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>
      <h2
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 18,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.2,
          textAlign: "center",
          marginBottom: 4,
        }}
      >
        {book.title}
      </h2>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 13,
          color: T.MUTED_BROWN,
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        {book.author}
      </p>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
        <GoldRule />
      </div>
      {generatedLabel && (
        <p
          className="m-0"
          style={{
            fontFamily: T.F_BODY,
            fontSize: 12,
            fontStyle: "italic",
            color: T.MUTED_BROWN,
            textAlign: "center",
          }}
          data-testid="siddhi-generated-at"
        >
          Received {generatedLabel}
        </p>
      )}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
        <Link
          to={`/sadhana?book=${book.id}`}
          style={{
            fontFamily: T.F_HEADING,
            fontSize: 11,
            letterSpacing: "0.12em",
            color: T.DARK_BROWN,
            textDecoration: "underline",
            textDecorationColor: T.GOLD,
          }}
        >
          ← Back to Sadhana
        </Link>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Identity statement — editable inline (mirrors Sadhana's EditableField
//  but standalone since it is a single field with ceremonial styling)
// ─────────────────────────────────────────────────────────────────────

function IdentityBlock({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const { status, flush } = useAutosave({ draft, savedValue: value, onSave });
  const saving = status === "saving";

  const save = async () => {
    if (await flush()) setEditing(false);
  };

  return (
    <div
      data-testid="siddhi-identity-block"
      style={{
        position: "relative",
        margin: "0 auto 40px",
        padding: "36px 40px",
        maxWidth: 720,
        background: "rgba(255,248,224,0.92)",
        border: `1px solid ${T.GOLD}`,
        borderRadius: 12,
        boxShadow: "0 10px 28px rgba(44,24,16,0.18)",
        textAlign: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -14,
          left: "50%",
          transform: "translateX(-50%)",
          padding: "4px 14px",
          background: T.PARCHMENT_LT,
          border: `1px solid ${T.GOLD}`,
          borderRadius: 999,
          fontFamily: T.F_HEADING,
          fontSize: 10,
          letterSpacing: "0.18em",
          color: T.GOLD,
        }}
      >
        IDENTITY · अस्मिता
      </div>
      {editing ? (
        <>
          <textarea
            data-testid="siddhi-identity-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={flush}
            rows={3}
            maxLength={600}
            style={{
              width: "100%",
              fontFamily: "Georgia, serif",
              fontStyle: "italic",
              fontSize: 20,
              lineHeight: 1.45,
              color: T.DARK_BROWN,
              textAlign: "center",
              background: "rgba(255,255,255,0.6)",
              border: `1px solid ${T.GOLD}`,
              borderRadius: 6,
              padding: 14,
              resize: "vertical",
            }}
          />
          <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 14 }}>
            <button
              type="button"
              data-testid="siddhi-identity-save"
              onClick={save}
              disabled={saving}
              style={{
                background: T.GOLD, border: 0, color: "#fff",
                padding: "6px 20px", borderRadius: 999, fontSize: 12,
                fontFamily: T.F_HEADING, letterSpacing: "0.10em",
                cursor: saving ? "wait" : "pointer",
              }}
            >
              {saving ? "Saving…" : status === "error" ? "Retry" : "Done"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              style={{
                background: "none", border: `1px solid ${T.MUTED_BROWN}`,
                color: T.MUTED_BROWN, padding: "6px 20px",
                borderRadius: 999, fontSize: 12, cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
          <p aria-live="polite" data-testid="siddhi-identity-status" style={{ margin: "10px 0 0", fontSize: 12, fontFamily: T.F_BODY, color: status === "error" ? "#8B3A2E" : T.MUTED_BROWN }}>
            {status === "saving" ? "Saving…" : status === "saved" ? "Saved ✓" : status === "error" ? "Couldn't save — retry" : ""}
          </p>
        </>
      ) : (
        <>
          <p
            data-testid="siddhi-identity-text"
            style={{
              margin: 0,
              fontFamily: T.F_DISPLAY,
              fontSize: 24,
              lineHeight: 1.45,
              color: T.DARK_BROWN,
              fontWeight: 500,
            }}
          >
            {value || <em style={{ color: T.MUTED_BROWN }}>—</em>}
          </p>
          <button
            type="button"
            data-testid="siddhi-identity-refine"
            onClick={() => setEditing(true)}
            style={{
              marginTop: 14,
              background: "none",
              border: 0,
              color: T.MUTED_BROWN,
              fontFamily: T.F_BODY,
              fontStyle: "italic",
              fontSize: 12,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            refine in your own words
          </button>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  WOOP stanza card
// ─────────────────────────────────────────────────────────────────────

const WOOP_LABELS = {
  wish:     { label: "WISH · संकल्प",     sub: "What you set out to become" },
  outcome:  { label: "OUTCOME · परिणाम",  sub: "What actually shifted" },
  obstacle: { label: "OBSTACLE · विघ्न",  sub: "What got in the way" },
  plan:     { label: "PLAN · योजना",       sub: "The next step, quietly" },
};

function WoopCard({ stanzaKey, text, lang }) {
  const base = WOOP_LABELS[stanzaKey];
  const tr = siddhiText(lang)[stanzaKey];
  const meta = base && (lang || "en") !== "en" && tr ? { label: `${tr[0]} · ${base.label.split(" · ")[1]}`, sub: tr[1] } : base;
  if (!meta || !text) return null;
  return (
    <div
      data-testid={`siddhi-woop-${stanzaKey}`}
      style={{
        background: CARD_BG,
        border: CARD_BORDER,
        borderRadius: CARD_RADIUS,
        padding: "22px 26px",
        boxShadow: CARD_SHADOW,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span
          style={{
            fontFamily: T.F_HEADING,
            fontSize: 11,
            letterSpacing: "0.16em",
            color: T.GOLD,
          }}
        >
          {meta.label}
        </span>
        <span
          style={{
            fontFamily: T.F_BODY,
            fontStyle: "italic",
            fontSize: 12,
            color: T.MUTED_BROWN,
          }}
        >
          {meta.sub}
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontFamily: T.F_BODY,
          fontSize: 15,
          lineHeight: 1.65,
          color: T.DARK_BROWN,
        }}
      >
        {text}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Narrative-journal input — appears BELOW the WOOP stanzas so the
//  reader can add a personal note and regenerate a richer retrospective.
// ─────────────────────────────────────────────────────────────────────

function JournalPanel({ book, lang, authHeaders, onRegenerated, onGate }) {
  const [journal, setJournal] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    const text = journal.trim();
    if (!text) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API_BASE}/siddhi/generate`,
        {
          book: { id: String(book.id), title: book.title || "", author: book.author || "" },
          targetLanguage: lang,
          narrative_journal: text,
        },
        { headers: authHeaders ? authHeaders() : {}, timeout: AXIOS_TIMEOUT_MS },
      );
      onRegenerated(res.data);
      setJournal("");
    } catch (e) {
      // 428 → the CraftPrint state changed (e.g. reflections wiped) since the
      // original Siddhi was generated. Elevate to the top-level gate view so
      // the reader learns why, instead of a silent inline error.
      if (e?.response?.status === 428 && typeof onGate === "function") {
        onGate(e.response.data?.detail || { reason: "reflections_incomplete" });
      } else {
        setErr(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="siddhi-journal-panel"
      style={{
        margin: "36px auto 0",
        maxWidth: 720,
        padding: "22px 26px",
        background: "rgba(255,248,224,0.7)",
        border: `1px dashed ${T.GOLD}`,
        borderRadius: CARD_RADIUS,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 11,
          letterSpacing: "0.16em",
          color: T.GOLD,
          marginBottom: 4,
        }}
      >
        A NOTE, IF YOU WISH · वैकल्पिक
      </p>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 13,
          color: T.MUTED_BROWN,
          marginBottom: 12,
        }}
      >
        Add a short reflection in your own voice — it will colour a fresh
        Siddhi drawn from the same reflections.
      </p>
      <textarea
        data-testid="siddhi-journal-textarea"
        value={journal}
        onChange={(e) => setJournal(e.target.value)}
        rows={3}
        maxLength={1200}
        placeholder="A sentence, a memory, a turning point — anything true."
        style={{
          width: "100%",
          fontFamily: T.F_BODY,
          fontSize: 14,
          lineHeight: 1.55,
          color: T.DARK_BROWN,
          background: "rgba(255,255,255,0.55)",
          border: `1px solid ${T.GOLD}`,
          borderRadius: 6,
          padding: 12,
          resize: "vertical",
        }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <span style={{ fontFamily: T.F_BODY, fontSize: 12, color: T.MUTED_BROWN, fontStyle: "italic" }}>
          {err ? "Could not regenerate — try again in a moment." : `${journal.length}/1200`}
        </span>
        <button
          type="button"
          data-testid="siddhi-journal-regenerate"
          onClick={submit}
          disabled={busy || !journal.trim()}
          style={{
            padding: "8px 22px",
            fontFamily: T.F_HEADING,
            fontSize: 12,
            letterSpacing: "0.10em",
            color: journal.trim() ? "#fff" : "rgba(61,35,20,0.45)",
            background: journal.trim() ? T.GOLD : "rgba(240,230,204,0.7)",
            border: `1px solid ${T.GOLD}`,
            borderRadius: 999,
            cursor: busy ? "wait" : (journal.trim() ? "pointer" : "not-allowed"),
          }}
        >
          {busy ? "Distilling…" : "Add note & receive again"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Practice actuals — non-punitive kept-days per committed practice,
//  read from the Sadhana completion tracker. Temple tone: no scoring.
// ─────────────────────────────────────────────────────────────────────

function ActualsStrip({ practices }) {
  if (!Array.isArray(practices) || practices.length === 0) return null;
  const anyKept = practices.some((p) => (p.days_kept || 0) > 0);
  return (
    <div
      data-testid="siddhi-actuals"
      style={{
        margin: "0 auto 34px",
        maxWidth: 720,
        padding: "18px 22px",
        background: "rgba(255,248,224,0.7)",
        border: "1px dashed rgba(201,168,76,0.55)",
        borderRadius: 10,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_HEADING, fontSize: 11, letterSpacing: "0.16em",
          color: T.GOLD, textAlign: "center", marginBottom: 14,
        }}
      >
        WHAT YOU KEPT · अभ्यास
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {practices.map((p) => (
          <div
            key={p.idea_id}
            data-testid={`siddhi-actual-${p.idea_id}`}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}
          >
            <span style={{ fontFamily: T.F_BODY, fontSize: 14, color: T.DARK_BROWN, minWidth: 0, flex: 1 }}>
              {p.title || `Practice ${p.idea_id}`}
            </span>
            <span style={{ fontFamily: T.F_HEADING, fontSize: 12, letterSpacing: "0.04em", color: T.MUTED_BROWN, whiteSpace: "nowrap" }}>
              {p.days_kept || 0} of {p.target || SADHANA_TARGET_DAYS} days kept
            </span>
          </div>
        ))}
      </div>
      {!anyKept && (
        <p
          className="m-0"
          style={{ marginTop: 12, textAlign: "center", fontFamily: T.F_BODY, fontStyle: "italic", fontSize: 12, color: T.MUTED_BROWN }}
        >
          Your practice is quiet so far — every kept day counts, whenever it comes.
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────────────────────────────

export default function SiddhiPage() {
  const [params] = useSearchParams();
  const bookParam = params.get("book");
  const book = useMemo(() => resolveBook(bookParam), [bookParam]);
  const [lang] = useLanguage();
  const { isAuthenticated, authHeaders, user, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(false);
  const [siddhi, setSiddhi] = useState(null); // {identity_statement, woop, install_id, generated_at, ...}
  const [gate, setGate] = useState(null);     // { reason, message, missing }
  const [error, setError] = useState(false);
  const [actuals, setActuals] = useState([]); // [{idea_id, title, days_kept, target}]

  // Load per-practice kept-days actuals from the Sadhana completion tracker.
  useEffect(() => {
    if (!book) { setActuals([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const g = await axios.get(`${API_BASE}/craftprints`, {
          params: { book_id: String(book.id), targetLanguage: (lang || "en").toLowerCase() },
          headers: authHeaders(),
          timeout: AXIOS_TIMEOUT_MS,
        });
        if (cancelled || !g.data?.found) { if (!cancelled) setActuals([]); return; }
        const prints = Array.isArray(g.data.craftprints) ? g.data.craftprints : [];
        const ideaIds = prints.map((cp) => cp.idea_id);
        const comps = isAuthenticated
          ? (g.data.completions || {})
          : readAnonCompletions(book.id, (lang || "en").toLowerCase(), ideaIds);
        setActuals(prints.map((cp) => {
          const s = comps[String(cp.idea_id)] || {};
          return {
            idea_id: cp.idea_id,
            title: cp.title || "",
            days_kept: s.days_kept || 0,
            target: s.target || SADHANA_TARGET_DAYS,
          };
        }));
      } catch (e) {
        if (!cancelled) setActuals([]);
      }
    })();
    return () => { cancelled = true; };
  }, [book, lang, isAuthenticated, authHeaders]);

  const load = useCallback(async () => {
    if (!book || authLoading) return;
    setLoading(true);
    setError(false);
    setGate(null);
    setSiddhi(null);
    // Step 1: try to read persisted first (fast path, no LLM cost).
    const installId = `${user?.id || "anon"}:${book.id}:${(lang || "en").toLowerCase()}`;
    try {
      const g = await axios.get(`${API_BASE}/siddhi/${installId}`, { headers: authHeaders(), timeout: AXIOS_TIMEOUT_MS });
      if (g.data?.found) {
        setSiddhi(g.data);
        setLoading(false);
        return;
      }
    } catch (e) {
      // Never regenerate on a failed read — that would overwrite the Siddhi.
      setError(true);
      setLoading(false);
      return;
    }
    // Step 2: no persisted Siddhi — attempt generate. Backend will 428
    // if the CraftPrint isn't ready.
    const generate = () => axios.post(
      `${API_BASE}/siddhi/generate`,
      {
        book: { id: String(book.id), title: book.title || "", author: book.author || "" },
        targetLanguage: (lang || "en").toLowerCase(),
      },
      { headers: authHeaders(), timeout: GENERATE_TIMEOUT_MS },
    );
    const readStored = async () => {
      const g = await axios.get(`${API_BASE}/siddhi/${installId}`, { headers: authHeaders(), timeout: AXIOS_TIMEOUT_MS });
      return g.data?.found ? g.data : null;
    };
    try {
      let res;
      try {
        res = await generate();
      } catch (first) {
        if (first?.response?.status === 428) throw first;
        // One silent retry: the first attempt may still finish server-side, so read before re-asking.
        await new Promise((r) => setTimeout(r, 4000));
        const stored = await readStored().catch(() => null);
        if (stored) { setSiddhi(stored); return; }
        res = await generate();
      }
      // Re-read the stored record so the first view has evidence + reflections, like a reload.
      const full = await readStored();
      setSiddhi(full || res.data);
    } catch (e) {
      if (e?.response?.status === 428) {
        setGate(e.response.data?.detail || { reason: "unknown" });
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [book, lang, user?.id, authHeaders, authLoading]);

  useEffect(() => { load(); }, [load]);

  const viewLang = (lang || "en").toLowerCase();
  const st = siddhiText(viewLang);
  const [rewriting, setRewriting] = useState(false);
  // Same journey, new language: a new Siddhi version; nothing is reset.
  const rewriteInLang = useCallback(async () => {
    setRewriting(true);
    try {
      await axios.post(`${API_BASE}/siddhi/generate`, {
        book: { id: String(book.id), title: book.title || "", author: book.author || "" }, targetLanguage: viewLang,
      }, { headers: authHeaders(), timeout: GENERATE_TIMEOUT_MS });
      await load();
    } catch (e) {
      setError(true);
    } finally {
      setRewriting(false);
    }
  }, [book, viewLang, authHeaders, load]);

  // Patch identity statement in user's voice.
  const saveIdentity = useCallback(async (newIdentity) => {
    if (!siddhi?.install_id) return false;
    try {
      const res = await axios.patch(
        `${API_BASE}/siddhi/${siddhi.install_id}/identity`,
        { identity_statement: newIdentity },
        { headers: authHeaders(), timeout: AXIOS_TIMEOUT_MS },
      );
      if (res.data?.identity_statement) {
        setSiddhi((s) => ({ ...s, identity_statement: res.data.identity_statement }));
      }
      return true;
    } catch (e) {
      return false;
    }
  }, [siddhi, authHeaders]);

  return (
    <div
      data-testid="siddhi-page"
      className="min-h-screen flex flex-col siddhi-page-root"
      style={{ background: "transparent" }}
    >
      <ParchmentBackground />
      <Navigation activePath={R.siddhi} />

      <main
        className="flex-1 w-full"
        style={{ paddingTop: 88, paddingBottom: 64, background: "transparent" }}
      >
        {/* State (a) — no book param */}
        {!book && (
          <div className="siddhi-empty-wrap" style={{ position: "relative", minHeight: 1025 }}>
            <StageEmptyStateFlank stage="siddhi" />
            <EmptyState />
          </div>
        )}

        {/* States with a book */}
        {book && (
          <div
            className="siddhi-shell"
            style={{
              position: "relative",
              maxWidth: 1160,
              margin: "0 auto",
              padding: "0 32px",
              display: "grid",
              gridTemplateColumns: "260px 1fr",
              gap: 40,
              alignItems: "flex-start",
            }}
          >
            <CurrentBookPanel book={book} generatedAt={siddhi?.generated_at} />

            <section>
              {loading && <LoadingBanner bookTitle={book.title} />}
              {error && !loading && <ErrorState bookTitle={book.title} onRetry={load} />}

              {/* State (b) — gate (428) */}
              {gate && !loading && <NeedsSadhana book={book} gate={gate} />}

              {/* State (e) — persisted Siddhi ready */}
              {siddhi && !loading && !error && (
                <div data-testid="siddhi-scroll">
                  <div style={{ textAlign: "center", marginBottom: 26 }}>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                      <GoldRule wide />
                    </div>
                    <StageKicker stage="siddhi" text="SIDDHI · सिद्धि" testId="siddhi-header-kicker" />
                    {siddhi.early && (
                      <div>
                        <span className="siddhi-early-label" data-testid="siddhi-early-label">
                          {st.early(siddhi.evidence?.days_kept ?? siddhi.kept_days_at_generation ?? 0)}
                        </span>
                      </div>
                    )}
                    <h1
                      className="m-0 siddhi-header-h1"
                      style={{
                        fontFamily: T.F_DISPLAY,
                        fontSize: 40,
                        fontWeight: 700,
                        color: T.DARK_BROWN,
                        lineHeight: 1.1,
                        marginBottom: 10,
                      }}
                    >
                      {siddhi.early ? st.h1Early : st.h1}
                    </h1>
                    <p
                      className="m-0"
                      style={{
                        fontFamily: T.F_BODY,
                        fontStyle: "italic",
                        fontSize: 16,
                        color: T.MUTED_BROWN,
                        lineHeight: 1.55,
                        maxWidth: 620,
                        margin: "0 auto",
                      }}
                    >
                      {st.subPre} <strong>{book.title}</strong>.{" "}
                      {st.subPost}
                      {siddhi.early ? ` ${st.subEarly}` : ""}
                    </p>
                  </div>

                  {(siddhi.lang || "en") !== viewLang && (
                    <div className="adp-panel real-siddhi" data-testid="siddhi-other-language">
                      <p className="adp-retro-guess">{st.writtenIn(LANG_LABEL[siddhi.lang || "en"])}</p>
                      <button type="button" className="dcp-btn-save" onClick={rewriteInLang} disabled={rewriting} data-testid="siddhi-rewrite-language-btn">
                        {rewriting ? st.rewriting : st.rewrite(LANG_LABEL[viewLang])}
                      </button>
                    </div>
                  )}
                  <RealSiddhiRetro siddhi={siddhi} onSaveIdentity={saveIdentity} lang={viewLang} />

                  <div data-testid="siddhi-woop-grid" className="siddhi-woop-compact">
                    <WoopCard stanzaKey="wish"     text={siddhi.woop?.wish} lang={viewLang} />
                    <WoopCard stanzaKey="outcome"  text={siddhi.woop?.outcome} lang={viewLang} />
                    <WoopCard stanzaKey="plan"     text={siddhi.woop?.plan} lang={viewLang} />
                  </div>

                  <JournalPanel
                    book={book}
                    lang={(lang || "en").toLowerCase()}
                    authHeaders={authHeaders}
                    onRegenerated={() => load()}
                    onGate={(detail) => { setSiddhi(null); setGate(detail); }}
                  />

                  <SealAsChapter book={book} identity={siddhi.identity_statement} user={user} authHeaders={authHeaders} />

                  <EarlierVersions versions={siddhi.versions} lang={viewLang} />

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 48,
                      paddingTop: 24,
                      borderTop: `1px dashed ${T.GOLD}`,
                    }}
                  >
                    <Link
                      to="/library"
                      data-testid="siddhi-begin-another"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "12px 28px",
                        fontFamily: T.F_HEADING,
                        fontSize: 13,
                        letterSpacing: "0.10em",
                        color: T.DARK_BROWN,
                        background: "rgba(240,230,204,0.8)",
                        border: `1px solid ${T.GOLD}`,
                        borderRadius: 999,
                        textDecoration: "none",
                      }}
                    >
                      Begin another journey <ArrowRight size={14} strokeWidth={1.75} />
                    </Link>
                    <p
                      className="m-0"
                      style={{
                        fontFamily: T.F_BODY,
                        fontStyle: "italic",
                        fontSize: 12,
                        color: T.MUTED_BROWN,
                        letterSpacing: "0.02em",
                      }}
                    >
                      The loop returns to Jigyasa — a new question, another book.
                    </p>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {book && <StageBottomBanner stage="siddhi" />}
      </main>

      <Footer activePath={R.siddhi} />

      {/* Responsive pass (<900px) — mirror SadhanaPage */}
      <style>{`
        .siddhi-page-root { overflow-x: hidden; }
        @media (max-width: 899px) {
          .stage-flank, .stage-bottom-banner, .stage-empty-flank { display: none !important; }
          .siddhi-empty-wrap { min-height: 0 !important; }
          .siddhi-shell {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
            padding: 0 20px !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .siddhi-shell > * { min-width: 0 !important; }
          .siddhi-current-book {
            position: static !important;
            max-height: none !important;
            max-width: 320px !important;
            margin: 0 auto !important;
            width: 100% !important;
          }
          [data-testid="siddhi-woop-grid"] {
            grid-template-columns: 1fr !important;
          }
          .siddhi-header-h1 { font-size: 30px !important; line-height: 1.15 !important; }
          .siddhi-empty-h1  { font-size: 32px !important; }
        }
      `}</style>
    </div>
  );
}
